"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Home, ArrowLeft, Search, Code2, Briefcase, Mail } from "lucide-react";
import Link from "next/link";
import GlassCard from "@/components/creations/glass-card";

export default function NotFound() {
  const router = useRouter();
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      router.push("/");
    }
  }, [countdown, router]);

  const quickLinks = [
    { icon: Home, label: "Home", href: "/" },
    { icon: Code2, label: "Projects", href: "/projects" },
    { icon: Briefcase, label: "About", href: "/about" },
    { icon: Mail, label: "Contact", href: "/contact" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-linear-to-br from-background via-background to-primary/5">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-destructive/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        {/* Main 404 Card */}
        <GlassCard
          variant="premium"
          blur="xl"
          glow="strong"
          shine="medium"
          animated
          className="p-8 md:p-12 mb-6"
        >
          <div className="text-center mb-8">
            {/* 404 Display */}
            <div className="mb-6">
              <h1 className="text-outline-bg-primary-glow text-8xl md:text-9xl font-black mb-2">
                404
              </h1>
              <div className="h-1 w-32 bg-linear-to-r from-transparent via-primary to-transparent mx-auto mb-4" />
            </div>

            {/* Message */}
            <h2 className="text-outline-fg-primary text-2xl md:text-3xl font-bold mb-4">
              Page Not Found
            </h2>
            <p className="text-foreground/70 text-lg max-w-md mx-auto mb-6">
              Oops! Looks like this page took a coffee break and never came
              back. Let's get you back on track.
            </p>

            {/* Auto-redirect countdown */}
            <div className="inline-flex items-center gap-2 text-sm text-foreground/60 mb-8">
              <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
              Redirecting to home in{" "}
              <span className="text-outline-primary-fg font-bold">
                {countdown}s
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link href="/">
              <button className="group flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all hover:scale-105 shadow-lg hover:shadow-primary/50 w-full sm:w-auto">
                <Home className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                <span className="font-semibold">Go Home</span>
              </button>
            </Link>

            <button
              onClick={() => router.back()}
              className="group flex items-center justify-center gap-2 px-6 py-3 border-2 border-primary/50 text-foreground rounded-lg hover:bg-primary/10 transition-all hover:scale-105 w-full sm:w-auto"
            >
              <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Go Back</span>
            </button>
          </div>

          {/* Search Suggestion */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/40" />
            <input
              type="text"
              placeholder="Search for projects, skills, or articles..."
              className="w-full pl-12 pr-4 py-3 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  router.push(`/search?q=${e.currentTarget.value}`);
                }
              }}
            />
          </div>
        </GlassCard>

        {/* Quick Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickLinks.map((link) => (
            <Link key={link.href} href={link.href}>
              <GlassCard
                variant="subtle"
                blur="md"
                glow="subtle"
                hover
                className="p-6 text-center group cursor-pointer"
              >
                <link.icon className="h-8 w-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                <p className="text-outline-fg-primary-soft text-sm font-semibold">
                  {link.label}
                </p>
              </GlassCard>
            </Link>
          ))}
        </div>

        {/* Developer Easter Egg */}
        <div className="mt-8 text-center">
          <GlassCard
            variant="frosted"
            blur="sm"
            className="inline-block px-6 py-3"
          >
            <p className="text-xs text-foreground/50 font-mono">
              <span className="text-outline-primary-fg-soft">Error Code:</span>{" "}
              HTTP_404_NOT_FOUND | Brian Otieno © {new Date().getFullYear()}
            </p>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
